import * as React from "react"
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Input,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@nexus/ui"
import { toast } from "sonner"
import type { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import type { BulkActionState } from "../../../hooks/useOpportunityBulkAction"
import type { OpportunityTabKey } from "../../../types/opportunities.types"

type ButtonIconStart = React.ComponentProps<typeof Button>["iconStart"]

export interface BulkActionBarProps {
  selectedCount: number
  activeTab?: OpportunityTabKey
  scriptsState: BulkActionState
  rejectState: BulkActionState
  onClearSelection: () => void
  onReject: (reason: string) => void
  onGenerateScripts: () => void
  onDownloadCsv?: () => void
  isDownloadingCsv?: boolean
}

function isActionBusy(state: BulkActionState): boolean {
  return state.type === "pending" || state.type === "polling"
}

/** Compact label for the bar — detailed progress lives in the task-progress sidebar. */
function scriptsBarLabel(state: BulkActionState, t: TFunction): string | null {
  if (state.type === "idle" || state.type === "expired") return null
  if (state.action !== "generate-scripts") return null
  if (state.type === "pending") return t("common.starting")
  if (state.type === "polling") {
    // While writing scripts we already know the totals — show "12 / 50".
    if (state.total != null && state.total > 0) {
      return t("opportunities.bulk.scriptsProgress", {
        completed: state.completed ?? 0,
        total: state.total,
      })
    }
    // Prep phases (resolveConfig → allocateScripts) don't yet have totals, so
    // surface the phase headline so users see actual progress, not "Starting…".
    if (state.phase) {
      return t(`opportunities.bulk.scriptsPhase.${state.phase}.title`)
    }
  }
  return null
}

function isScriptsJobActive(state: BulkActionState): boolean {
  if (!isActionBusy(state)) return false
  return state.type !== "idle" && state.type !== "expired" && state.action === "generate-scripts"
}

export function BulkActionBar({
  selectedCount,
  activeTab,
  scriptsState,
  rejectState,
  onClearSelection,
  onReject,
  onGenerateScripts,
  onDownloadCsv,
  isDownloadingCsv = false,
}: BulkActionBarProps) {
  const { t } = useTranslation()
  const [rejectOpen, setRejectOpen] = React.useState(false)
  const [rejectReason, setRejectReason] = React.useState("")
  const [confirmOpen, setConfirmOpen] = React.useState(false)

  const REJECT_REASON_MAX_LENGTH = 500

  const showScripts = activeTab !== "completed_scripts" && activeTab !== "orders" && activeTab !== "rejected"
  const showReject = activeTab !== "rejected"
  const showDownloadCsv = activeTab === "rejected" && onDownloadCsv != null

  // Visibility is selection-driven. Long-running script generation is shown
  // in the unified task-progress sidebar so the bar can disappear when the
  // user clears their selection without losing job feedback. We still keep the
  // bar open briefly for synchronous reject state so users see the result inline.
  const visible = selectedCount > 0 || (showReject && rejectState.type !== "idle")

  if (!visible) return null

  const scriptsBusy = showScripts && isActionBusy(scriptsState)
  const rejectBusy = showReject && isActionBusy(rejectState)
  const anyBusy = scriptsBusy || rejectBusy || isDownloadingCsv

  const handleRejectSubmit = () => {
    if (!rejectReason.trim()) return
    setConfirmOpen(true)
  }

  const handleRejectConfirm = () => {
    const reason = rejectReason.trim()
    const count = selectedCount
    setRejectReason("")
    setRejectOpen(false)
    setConfirmOpen(false)

    onReject(reason)
    toast.success(t("opportunities.bulk.rejectSuccess", { count }))
  }

  const scriptsActive = isScriptsJobActive(scriptsState)
  const selectedLabel = scriptsActive
    ? t("opportunities.bulk.generatingScripts")
    : selectedCount === 1
      ? t("opportunities.bulk.selected")
      : t("opportunities.bulk.selectedPlural")
  const selectedCompactLabel = scriptsActive
    ? t("opportunities.bulk.generatingScripts")
    : t("opportunities.bulk.selectedCompact")

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 flex justify-center px-3 pb-[max(1rem,env(safe-area-inset-bottom))] animate-in fade-in slide-in-from-bottom-2 duration-300 sm:bottom-4 sm:px-4 sm:pb-0"
      role="region"
      aria-label={
        selectedCount === 1
          ? t("opportunities.bulk.barAriaOne")
          : t("opportunities.bulk.barAria", { count: selectedCount })
      }
    >
      <div className="flex w-full max-w-lg flex-col gap-2 rounded-md border border-border bg-background px-3 py-3 shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)] sm:w-auto sm:max-w-xl sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-0 sm:px-4 sm:py-2 dark:bg-muted">

        {/* Selection info */}
        <div className="flex w-full min-w-0 items-center justify-between gap-2 sm:w-auto sm:justify-start">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-medium tabular-nums text-primary-foreground">
              {selectedCount}
            </span>
            <span className="truncate text-sm font-medium text-foreground sm:hidden">
              {selectedCompactLabel}
            </span>
            <span className="hidden truncate text-sm font-medium text-foreground sm:inline">
              {selectedLabel}
            </span>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={onClearSelection}
                  className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label={t("opportunities.bulk.clearSelection")}
                >
                  <Icon name="x" size="xs" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{t("opportunities.bulk.clearSelection")}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {showReject && rejectOpen ? (
          <div className="flex w-full min-w-0 flex-col gap-2 animate-in fade-in slide-in-from-right-2 duration-200 min-[400px]:flex-row min-[400px]:items-center min-[400px]:gap-1.5 sm:w-auto">
            <Input
              placeholder={t("opportunities.bulk.rejectPlaceholder")}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleRejectSubmit() }}
              maxLength={REJECT_REASON_MAX_LENGTH}
              className="h-9 min-w-0 w-full text-xs sm:h-8 sm:w-48"
              autoFocus
              aria-label={t("opportunities.bulk.rejectPlaceholder")}
            />
            <div className="flex items-center gap-1.5">
              <Button
                variant="destructive"
                size="sm"
                disabled={!rejectReason.trim() || isActionBusy(rejectState)}
                onClick={handleRejectSubmit}
                className="min-h-9 flex-1 text-xs sm:min-h-0 sm:flex-none"
              >
                {t("opportunities.bulk.reject")}
              </Button>
              <button
                type="button"
                onClick={() => { setRejectOpen(false); setRejectReason("") }}
                className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label={t("common.cancel")}
              >
                <Icon name="x" size="xs" />
              </button>
            </div>
          </div>
        ) : (
          <div
            className={cn(
              "grid w-full gap-2 sm:flex sm:w-auto sm:items-center sm:gap-1.5",
              showScripts && showReject ? "grid-cols-2" : "grid-cols-1",
            )}
          >
            {showDownloadCsv && (
              <Button
                variant="outline"
                size="sm"
                iconStart={isDownloadingCsv ? undefined : "download"}
                onClick={onDownloadCsv}
                disabled={isDownloadingCsv}
                className="min-h-9 w-full text-xs sm:min-h-0 sm:w-auto"
                data-testid="bulk-download-csv"
              >
                {isDownloadingCsv ? (
                  <>
                    <Icon name="loader" size="xs" className="animate-spin shrink-0" />
                    <span className="truncate">{t("opportunities.bulk.downloadingCsv")}</span>
                  </>
                ) : (
                  t("opportunities.bulk.downloadCsv")
                )}
              </Button>
            )}

            {showScripts && (
              <ActionButton
                label={t("opportunities.bulk.generateScripts")}
                busyLabel={scriptsBarLabel(scriptsState, t) ?? t("opportunities.bulk.generatingScripts")}
                busyHint={
                  isScriptsJobActive(scriptsState)
                    ? t("opportunities.bulk.scriptsBarHint")
                    : undefined
                }
                doneLabel={t("opportunities.bulk.scriptsGenerated")}
                icon="sparkles"
                state={scriptsState}
                disabled={anyBusy}
                onClick={onGenerateScripts}
                className="w-full sm:w-auto"
              />
            )}

            {showReject && (
              <ActionButton
                label={t("opportunities.bulk.reject")}
                busyLabel={t("opportunities.bulk.rejecting")}
                icon="x-circle"
                variant="outline"
                state={rejectState}
                disabled={anyBusy && !isActionBusy(rejectState)}
                onClick={() => setRejectOpen(true)}
                className="w-full text-destructive hover:bg-destructive/10 hover:text-destructive sm:w-auto"
              />
            )}
          </div>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("opportunities.bulk.confirmRejectTitle")}</DialogTitle>
            <DialogDescription>
              {t("opportunities.bulk.confirmRejectDescription", { count: selectedCount })}
            </DialogDescription>
          </DialogHeader>
          {rejectReason && (
            <div className="rounded-md border border-border bg-muted/50 px-3 py-2 text-sm text-foreground">
              {rejectReason}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button variant="destructive" onClick={handleRejectConfirm}>
              {t("opportunities.bulk.confirmReject", { count: selectedCount })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function ActionButton({
  label,
  busyLabel,
  busyHint,
  doneLabel,
  icon,
  variant,
  state,
  disabled,
  onClick,
  className,
}: {
  label: string
  busyLabel: string
  /** Shown on the activity card; keeps the bar label short during script jobs. */
  busyHint?: string
  doneLabel?: string
  icon: ButtonIconStart
  variant?: "destructive" | "outline"
  state: BulkActionState
  disabled: boolean
  onClick: () => void
  className?: string
}) {
  const layoutClass = cn("min-h-9 w-full text-xs sm:min-h-0 sm:w-auto", className)

  if (state.type === "pending" || state.type === "polling") {
    const ariaLabel = busyHint ? `${busyLabel}. ${busyHint}` : busyLabel
    return (
      <Button
        size="sm"
        variant="outline"
        disabled
        className={cn(layoutClass, "tabular-nums")}
        aria-label={ariaLabel}
        title={busyHint}
      >
        <Icon name="loader" size="xs" className="animate-spin shrink-0" />
        <span className="truncate">{busyLabel}</span>
      </Button>
    )
  }

  if (state.type === "done") {
    const { summary } = state
    const hasFails = summary.failed > 0
    return (
      <Button variant="outline" size="sm" disabled className={layoutClass}>
        <Icon
          name={hasFails ? "alert-circle" : "check-circle"}
          size="xs"
          className={hasFails ? "text-destructive" : "text-status-success"}
        />
        <span className="truncate">{doneLabel ?? label}</span>
        <span className="shrink-0 tabular-nums opacity-70">
          ({summary.done}/{summary.total})
        </span>
      </Button>
    )
  }

  if (state.type === "error") {
    return (
      <Button variant="destructive" size="sm" onClick={onClick} className={layoutClass}>
        <Icon name="alert-circle" size="xs" />
        <span className="truncate">{label}</span>
      </Button>
    )
  }

  return (
    <Button
      size="sm"
      variant={variant}
      onClick={onClick}
      disabled={disabled}
      iconStart={icon}
      className={layoutClass}
    >
      <span className="truncate">{label}</span>
    </Button>
  )
}
